/**
 * Guest Collection View Page
 * Public page for viewing and collaborating on shared collections via guest link
 * No authentication required
 */

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { getCollectionByGuestToken, getMarketplaceCollection, addItemAsGuest, removeItemAsGuest, toggleItemCheckGuest, updateItemImageAsGuest, updateItemAsGuest, GuestCollection, checkGuestVerification } from '../services/collectionSharingService';
import { getCachedImages } from '../utils/imageSearchCache';
import { getColorFromName, getInitials } from '../utils/imageHelpers';
import { getGuestFingerprint, storeGuestSession, getGuestDisplayName, storeGuestDisplayName, getCurrentGuestUserId } from '../services/guestAuth';
import { getStoredGuestToken } from '../utils/guestFingerprint';
import { useFontSize, FontSize } from '../hooks/useFontSize';
import SharedCollectionHeader from '../components/SharedCollectionHeader';
import ImageSelectionModal from '../components/ImageSelectionModal';
import { UnifiedItemEditModal } from '../components/itemEdit';
import WhySignUpModal from '../components/WhySignUpModal';
import GuestProfileModal from '../components/GuestProfileModal';
import GuestEmailVerification from '../components/GuestEmailVerification';
import { InventoryContent } from '../components/InventoryContent';
import { ViewMode } from '../types/inventory';
import '../styles/pages/guestCollectionView.css';
import '../styles/components/inventory.css'; // Reuse inventory card styles
import '../styles/header.css'; // Reuse header styles from main app
import '../styles/components/unifiedHeader.css'; // Font toggle signal bar styles
import ContactSellerModal from '../components/ContactSellerModal';

const GuestCollectionView: React.FC = () => {
  const { token, collectionId: collectionIdParam } = useParams<{ token?: string; collectionId?: string }>();
  const marketplaceId = collectionIdParam ? parseInt(collectionIdParam, 10) : null;
  const isMarketplaceDirect = !!marketplaceId && !token;
  const navigate = useNavigate();
  const { fontSize, setFontSize } = useFontSize();
  // Guest-local theme toggle — default to system preference, not the logged-in user's stored theme
  const [guestTheme, setGuestTheme] = useState<'light' | 'dark'>(() => {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });
  const savedThemePrefRef = React.useRef<string | null>(null);
  useEffect(() => {
    // Save the logged-in user's theme preference so we can restore it on unmount
    savedThemePrefRef.current = localStorage.getItem('theme-preference');
    // Temporarily override localStorage so ThemeProvider resolves to system theme
    localStorage.setItem('theme-preference', 'system');
    // Apply guest theme immediately
    const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', systemTheme);
    return () => {
      // Restore original preference when guest leaves
      if (savedThemePrefRef.current !== null) {
        localStorage.setItem('theme-preference', savedThemePrefRef.current);
      } else {
        localStorage.removeItem('theme-preference');
      }
      document.documentElement.setAttribute('data-theme',
        savedThemePrefRef.current === 'dark' ? 'dark'
          : savedThemePrefRef.current === 'light' ? 'light'
          : savedThemePrefRef.current === 'pastel' ? 'pastel'
          : window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
      );
    };
  }, []);
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', guestTheme);
  }, [guestTheme]);
  
  const [collection, setCollection] = useState<GuestCollection | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  
  // Email verification state
  const [requiresVerification, setRequiresVerification] = useState(false);
  const [verifiedEmail, setVerifiedEmail] = useState<string | null>(() => {
    // Check localStorage for previously verified email for this token
    return token ? localStorage.getItem(`guest_verified_${token}`) : null;
  });
  
  // Item edit modal (for both add and edit)
  const [showItemEditModal, setShowItemEditModal] = useState(false);
  const [editingItem, setEditingItem] = useState<any | null>(null); // null = adding new item
  const [newItemData, setNewItemData] = useState<{name: string, description: string}>({name: '', description: ''});
  
  // Guest name prompt
  const [guestName, setGuestName] = useState<string>(() => getGuestDisplayName() || '');
  const [showNamePrompt, setShowNamePrompt] = useState<boolean>(() => !getGuestDisplayName());
  
  // Sign up prompt
  const [showSignUpPrompt, setShowSignUpPrompt] = useState(false);
  const [interactionCount, setInteractionCount] = useState(0);
  
  // Thumbnail size for composition view — default to medium (list with medium thumbs)
  const [thumbnailSize, setThumbnailSize] = useState<'small' | 'medium' | 'large' | 'x-large'>('medium');
  
  // Marketplace item selection
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const [showReviewSheet, setShowReviewSheet] = useState(false);
  const [reviewItems, setReviewItems] = useState<Array<{ id: number; name: string; qty: number; maxQty: number; price: number; originalPrice: number }>>([]);

  // Contact Seller modal state
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactReviewItems, setContactReviewItems] = useState<Array<{ id: number; name: string; qty: number; maxQty: number; price: number; originalPrice: number }> | undefined>(undefined);

  // Lock body scroll when review sheet is open
  useEffect(() => {
    if (showReviewSheet) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [showReviewSheet]);
  
  // Shopping list mode: 'manage' or 'shop'
  const [stockChecklistMode, setStockChecklistMode] = useState<'manage' | 'shop'>('shop');
  const [showAllStockChecklistItems, setShowAllStockChecklistItems] = useState(true);
  const [pendingQuantityChanges, setPendingQuantityChanges] = useState<Record<number, number>>({});
  
  // Current guest fingerprint (cached for the session)
  const [currentFingerprint, setCurrentFingerprint] = useState<string | null>(null);
  
  // Current guest user ID (from localStorage)
  const [currentGuestUserId, setCurrentGuestUserId] = useState<number | null>(null);
  
  // Image selection modal state
  const [showImageSelectionModal, setShowImageSelectionModal] = useState(false);
  const [selectedItemForImage, setSelectedItemForImage] = useState<any | null>(null);
  const [imageSearchResults, setImageSearchResults] = useState<string[]>([]);
  const [imagePhotoData, setImagePhotoData] = useState<any[]>([]); // Store full Unsplash photo objects
  const [imageSearchLoading, setImageSearchLoading] = useState(false);
  
  // Why Sign Up modal state
  const [showWhySignUpModal, setShowWhySignUpModal] = useState(false);
  
  // Guest context menu state
  const [showGuestContextMenu, setShowGuestContextMenu] = useState(false);
  const [guestContextItem, setGuestContextItem] = useState<any | null>(null);
  
  // Guest profile modal state
  const [showGuestProfileModal, setShowGuestProfileModal] = useState(false);
  
  // Get fingerprint and guest user ID on mount
  useEffect(() => {
    getGuestFingerprint().then(fp => {
      setCurrentFingerprint(fp);
    });
    
    // Get stored guest user ID from localStorage
    const storedUserId = getCurrentGuestUserId();
    setCurrentGuestUserId(storedUserId);
  }, []);
  
  /**
   * Check if guest can delete an item
   * - Full edit permission allows deleting any item
   * - Collection owner can delete any item
   * - Guest can only delete items they own (matched by fingerprint OR guest_token)
   */
  const canDeleteItem = (item: any): boolean => {
    if (!collection) return false;
    
    // Full edit permission allows deleting any item
    if (collection.permission === 'full_edit') {
      return true;
    }
    
    // Collection owner can delete any item
    // Note: Owner might be accessing via their own guest link
    const isOwner = item.item_owner_id === collection.collection.owner_id;
    const userIdMatch = !!(currentGuestUserId && item.item_owner_user_id === currentGuestUserId);
    const fingerprintMatch = !!(currentFingerprint && item.item_owner_fingerprint === currentFingerprint);
    
    if (isOwner && (userIdMatch || fingerprintMatch)) {
      return true;
    }
    
    // Guest can only delete their own items
    // Primary: Match by user_id, Fallback: Match by fingerprint
    const ownsItem: boolean = userIdMatch || fingerprintMatch;
    return ownsItem;
  };

  /**
   * Check if guest can edit an item
   * - Edit permission: Can only edit items they created (matched by fingerprint OR guest_token)
   * - Full edit permission: Can edit any item
   */
  const canEditItem = (item: any): boolean => {
    if (!collection) {
      return false;
    }
    
    // Full edit permission allows editing any item
    if (collection.permission === 'full_edit') {
      return true;
    }
    
    // Edit permission: Can only edit items they own
    // Primary: Match by user_id (reliable, handles fingerprint collisions)
    // Fallback: Match by fingerprint (for backward compatibility with old items)
    const userIdMatch = !!(currentGuestUserId && item.item_owner_user_id === currentGuestUserId);
    const fingerprintMatch = !!(currentFingerprint && item.item_owner_fingerprint === currentFingerprint);
    return userIdMatch || fingerprintMatch;
  };

  /**
   * Open guest context menu - only if user can delete the item
   */
  const openGuestContextMenu = (item: any) => {
    if (!collection) return;
    
    const canEdit = collection.permission === 'edit' || collection.permission === 'full_edit';
    const isExpired = collection.expires_at && new Date(collection.expires_at) < new Date();
    
    if (!canEdit || isExpired || !canDeleteItem(item)) return;
    
    setGuestContextItem(item);
    setShowGuestContextMenu(true);
  };

  /**
   * Open image selection modal for an item
   */
  const handleOpenImageSelection = async (item: any) => {
    // Check if fingerprint is loaded
    if (!currentFingerprint) {
      console.warn('[Guest] Fingerprint not loaded yet, cannot open image selection');
      alert('Please wait a moment and try again');
      return;
    }
    
    
    // Set item and open modal immediately (like user mode)
    setSelectedItemForImage(item);
    setShowImageSelectionModal(true);
    setImageSearchLoading(true);
    setImageSearchResults([]);
    
    // Fetch images asynchronously AFTER modal is open (like user mode)
    try {
      const accessKey = process.env.REACT_APP_UNSPLASH_ACCESS_KEY;
      if (!accessKey) {
        console.error('[Guest] Unsplash access key not configured');
        setImageSearchLoading(false);
        return;
      }
      
      const { buildUnsplashSearchUrl } = await import('../utils/unsplashUtils');
      const searchUrl = buildUnsplashSearchUrl(item.name, accessKey);
      
      console.log('[Guest] Searching Unsplash for:', item.name);
      const response = await fetch(searchUrl);
      if (!response.ok) {
        throw new Error(`Unsplash API error: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (!data.results || !Array.isArray(data.results)) {
        console.warn('[Guest] Unexpected Unsplash response format:', data);
        setImageSearchLoading(false);
        return;
      }
      
      // Extract small URLs for display (like user mode)
      const imageUrls = data.results.map((photo: any) => photo.urls?.small).filter(Boolean);
      
      console.log('[Guest] Found', imageUrls.length, 'images');
      
      // Store photo data for getting metadata when user selects
      setImagePhotoData(data.results);
      setImageSearchResults(imageUrls);
      setImageSearchLoading(false);
    } catch (error) {
      console.error('[Guest] Error in handleOpenImageSelection:', error);
      setImageSearchLoading(false);
      setImageSearchResults([]);
    }
  };

  /**
   * Handle selecting an Unsplash image
   */
  const handleSelectSearchImage = async (imageUrl: string) => {
    console.log('[Guest] handleSelectSearchImage called with URL:', imageUrl);
    
    if (!imageUrl || typeof imageUrl !== 'string') {
      console.error('[Guest] Invalid image URL:', imageUrl);
      alert('Invalid image selected');
      return;
    }
    
    if (!token || !selectedItemForImage) {
      console.error('[Guest] Missing token or selected item');
      return;
    }
    
    if (!currentFingerprint) {
      console.error('[Guest] Fingerprint not loaded yet');
      alert('Please wait a moment and try again');
      return;
    }
    
    try {
      console.log('[Guest] Updating image for item:', selectedItemForImage.id);
      
      // Find the photo object that matches this URL
      const photo = imagePhotoData.find((p: any) => p.urls?.small === imageUrl);
      
      // Build URL with metadata for backend
      let fullImageUrl = imageUrl;
      if (photo) {
        const regularUrl = photo.urls?.regular || imageUrl;
        fullImageUrl = `${imageUrl}?photographer=${encodeURIComponent(photo.user?.name || 'Unknown')}&username=${encodeURIComponent(photo.user?.username || 'unknown')}&photo_id=${photo.id || ''}&regular_url=${encodeURIComponent(regularUrl)}`;
      }
      
      console.log('[Guest] Image URL with metadata:', fullImageUrl);
      
      const result = await updateItemImageAsGuest(token, selectedItemForImage.id, currentFingerprint, {
        imageUrl: fullImageUrl
      });
      
      console.log('[Guest] Image update result:', result);
      
      // Close modal and clear all state after successful update
      setShowImageSelectionModal(false);
      setSelectedItemForImage(null);
      setImageSearchResults([]);
      setImagePhotoData([]);
      setImageSearchLoading(false);
      
      console.log('[Guest] Reloading collection...');
      // Reload collection to show updated image
      await loadCollection(true);
      
      console.log('[Guest] Collection reloaded successfully');
    } catch (error: any) {
      console.error('[Guest] Error updating image:', error);
      console.error('[Guest] Error stack:', error.stack);
      setShowImageSelectionModal(false);
      setSelectedItemForImage(null);
      setImageSearchResults([]);
      setImagePhotoData([]);
      setImageSearchLoading(false);
      alert(error.message || 'Failed to update image');
    }
  };

  /**
   * Handle selecting initials (no image)
   */
  const handleSelectInitials = async () => {
    console.log('[Guest] handleSelectInitials called');
    
    if (!token || !selectedItemForImage) {
      console.error('[Guest] Missing token or selected item');
      return;
    }
    
    if (!currentFingerprint) {
      console.error('[Guest] Fingerprint not loaded yet');
      alert('Please wait a moment and try again');
      return;
    }
    
    try {
      console.log('[Guest] Setting initials for item:', selectedItemForImage.id);
      
      await updateItemImageAsGuest(token, selectedItemForImage.id, currentFingerprint, {
        useInitials: true
      });
      
      console.log('[Guest] Initials set successfully, reloading collection');
      
      // Close modal and clear all state after successful update
      setShowImageSelectionModal(false);
      setSelectedItemForImage(null);
      setImageSearchResults([]);
      setImagePhotoData([]);
      setImageSearchLoading(false);
      
      // Reload collection to show updated image
      await loadCollection(true);
      
      console.log('[Guest] Collection reloaded successfully');
    } catch (error: any) {
      console.error('[Guest] Error updating image:', error);
      setShowImageSelectionModal(false);
      setSelectedItemForImage(null);
      setImageSearchResults([]);
      setImagePhotoData([]);
      setImageSearchLoading(false);
      alert(error.message || 'Failed to update image');
    }
  };

  /**
   * Handle taking/uploading a photo
   * Note: File upload not implemented for guests yet
   */
  const handleTakePhoto = async (file: File) => {
    alert('Photo upload is not available for guest users. Please create a free account to upload your own photos.');
    // Could implement GCS upload for guests in the future
  };

  const loadCollection = useCallback(async (silent = false) => {
    if (!token && !isMarketplaceDirect) return;
    
    try {
      if (!silent) setLoading(true);
      else setRefreshing(true);
      
      // Add email parameter if we have a verified email
      const emailParam = verifiedEmail ? `?email=${encodeURIComponent(verifiedEmail)}` : '';
      
      const data = isMarketplaceDirect
        ? await getMarketplaceCollection(marketplaceId!)
        : await getCollectionByGuestToken(token! + emailParam);
      setCollection(data);
      setError(null);
      setRequiresVerification(false); // Successfully loaded, no verification needed
      
      // Show sign-up prompt after meaningful interaction
      if (!silent && !localStorage.getItem('guest_signup_prompt_shown')) {
        // Show after 5 seconds if they're actively viewing
        setTimeout(() => {
          if (!localStorage.getItem('guest_signup_dismissed')) {
            setShowSignUpPrompt(true);
          }
        }, 5000);
      }
    } catch (err: any) {
      // Check if error is due to email verification requirement
      if (err.message?.includes('Email verification required') || err.message?.includes('requires_verification')) {
        setRequiresVerification(true);
        setError(null); // Don't show error, show verification UI instead
      } else {
        // Only show error on initial load, not on silent refreshes
        // (don't replace working view with an error from a transient failure)
        if (!silent) {
          setError(err.message || 'Failed to load collection');
        } else {
          console.warn('[Guest] Silent refresh failed:', err.message);
        }
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token, isMarketplaceDirect, marketplaceId, verifiedEmail]);

  useEffect(() => {
    loadCollection();
  }, [loadCollection]);

  // Auto-refresh every 15 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      loadCollection(true);
    }, 15000);

    return () => clearInterval(interval);
  }, [loadCollection]);

  /**
   * Open modal to add new item
   */
  const handleOpenAddItem = () => {
    // Prompt for name if not set
    if (!guestName.trim()) {
      setShowNamePrompt(true);
      return;
    }
    
    setEditingItem(null); // null means adding new item
    setNewItemData({name: '', description: ''}); // Reset new item data
    setShowItemEditModal(true);
  };

  /**
   * Open modal to edit existing item
   */
  const handleOpenEditItem = (item: any) => {
    setEditingItem(item);
    setShowItemEditModal(true);
  };

  /**
   * Save item (add or edit)
   */
  const handleSaveItem = async (name: string, description: string) => {
    if (!token || !currentFingerprint) {
      throw new Error('Missing required data');
    }

    if (editingItem) {
      // Edit existing item
      await updateItemAsGuest(token, editingItem.id, currentFingerprint, {
        name,
        description
      });
    } else {
      // Add new item
      const existingToken = getStoredGuestToken();
      
      const result = await addItemAsGuest(token, {
        name,
        description: description || undefined,
        guest_name: guestName.trim()
      }, currentFingerprint, existingToken || undefined);
      
      // Store guest session if returned
      if (result.user_id && result.guest_token) {
        storeGuestSession(result.user_id, result.guest_token);
        setCurrentGuestUserId(result.user_id);
      }
      
      // Track interaction for sign-up prompt
      const newCount = interactionCount + 1;
      setInteractionCount(newCount);
      
      // Show sign-up after 2 interactions if not dismissed
      if (newCount >= 2 && !localStorage.getItem('guest_signup_prompt_shown') && !localStorage.getItem('guest_signup_dismissed')) {
        setTimeout(() => setShowSignUpPrompt(true), 1000);
      }
    }
    
    // Reload collection
    await loadCollection(true);
  };

  const handleRemoveItem = async (itemId: number) => {
    if (!token) return;
    
    if (!window.confirm('Remove this item?')) return;
    
    try {
      // Get device fingerprint for ownership verification
      const fingerprint = await getGuestFingerprint();
      await removeItemAsGuest(token, itemId, fingerprint);
      await loadCollection(true);
    } catch (err: any) {
      alert(err.message || 'Failed to remove item');
    }
  };

  const handleToggleCheck = async (itemId: number, currentStatus: boolean) => {
    if (!token) return;
    
    try {
      await toggleItemCheckGuest(token, itemId, !currentStatus);
      
      // Track interaction
      const newCount = interactionCount + 1;
      setInteractionCount(newCount);
      
      // Show sign-up after 3 check interactions
      if (newCount >= 3 && !localStorage.getItem('guest_signup_prompt_shown') && !localStorage.getItem('guest_signup_dismissed')) {
        setTimeout(() => setShowSignUpPrompt(true), 500);
      }
      
      // Optimistically update UI
      setCollection(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map(item =>
            item.id === itemId
              ? { ...item, is_checked: !currentStatus, checked_at: !currentStatus ? new Date().toISOString() : null }
              : item
          )
        };
      });
    } catch (err: any) {
      alert(err.message || 'Failed to update item');
      // Reload to get correct state
      await loadCollection(true);
    }
  };

  const handleQuantityChange = (itemId: number, newQuantity: number) => {
    // Store pending changes instead of saving immediately
    setPendingQuantityChanges(prev => ({ ...prev, [itemId]: newQuantity }));
  };

  // Marketplace: toggle item selection (skip out-of-stock items)
  const toggleItemSelection = (item: any) => {
    if ((item.quantity ?? 1) === 0) return;
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(item.id)) {
        next.delete(item.id);
      } else {
        next.add(item.id);
      }
      return next;
    });
  };

  // Marketplace: open review sheet from selected items
  const openReviewSheet = () => {
    if (!collection || selectedItems.size === 0) return;
    const items = collection.items.filter(i => selectedItems.has(i.id));
    setReviewItems(items.map(i => ({
      id: i.id,
      name: i.name,
      qty: 1,
      maxQty: i.quantity ?? 1,
      price: i.sale_price != null ? Number(i.sale_price) / 100 : 0,
      originalPrice: i.sale_price != null ? Number(i.sale_price) / 100 : 0,
    })));
    setShowReviewSheet(true);
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  // Handler for successful email verification
  const handleEmailVerified = (email: string) => {
    setVerifiedEmail(email);
    setRequiresVerification(false);
    // Reload collection with verified email
    loadCollection();
  };

  if (loading) {
    return (
      <div className="guest-collection-view">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading collection...</p>
        </div>
      </div>
    );
  }

  // Show email verification UI if required
  if (requiresVerification && token) {
    return <GuestEmailVerification shareToken={token} onVerified={handleEmailVerified} />;
  }

  if (error) {
    return (
      <div className="guest-collection-view">
        <div className="error-state">
          <svg className="error-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h2>Unable to Load Collection</h2>
          <p>{error}</p>
          <button onClick={() => loadCollection()} className="retry-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!collection) return null;

  const canEdit = collection.permission === 'edit' || collection.permission === 'full_edit';
  const isExpired = collection.expires_at && new Date(collection.expires_at) < new Date();


  return (
    <div className="guest-collection-view">
      {/* Header */}
      <div
        ref={(el) => {
          if (el) {
            const h = el.getBoundingClientRect().height;
            document.documentElement.style.setProperty('--guest-header-h', `${Math.ceil(h)}px`);
          }
        }}
        style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000, background: 'var(--color-bg-primary)', borderBottom: '1px solid var(--color-border)' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 1rem', position: 'relative' }}>
          {/* Sock icon — original size + animation */}
          <img src="/happy_sock.png" alt="Wandering Sock" className="unified-header-icon" />
          {/* Title + subtitle — absolute positioned for true page centering */}
          <div style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
            <div style={{ textAlign: 'center' }}>
              <div className="unified-header-title">
                {collection.collection.collection_type === 'marketplace'
                  ? `${collection.collection.owner_name}'s Depot`
                  : 'Wandering Sock'}
              </div>
              <div className="unified-header-subtitle">
                {collection.collection.collection_type === 'marketplace'
                  ? 'by Wandering Sock'
                  : `${collection.items.length} ${collection.items.length === 1 ? 'item' : 'items'}`}
              </div>
            </div>
          </div>
          {/* Font toggle — on the right */}
          <div className="unified-header-actions">
            <button
              onClick={() => {
                const sizes: FontSize[] = ['small', 'medium', 'large', 'x-large'];
                const currentIndex = sizes.indexOf(fontSize);
                const nextIndex = (currentIndex + 1) % sizes.length;
                setFontSize(sizes[nextIndex]);
              }}
              className="unified-header-font-toggle"
              title={`Font size: ${fontSize}`}
              aria-label="Change font size"
            >
              <span className="font-toggle-text">Aa</span>
              <span className="font-toggle-bars">
                <span className={`bar bar-1 ${['small','medium','large','x-large'].indexOf(fontSize) >= 0 ? 'active' : ''}`}></span>
                <span className={`bar bar-2 ${['medium','large','x-large'].indexOf(fontSize) >= 0 ? 'active' : ''}`}></span>
                <span className={`bar bar-3 ${['large','x-large'].indexOf(fontSize) >= 0 ? 'active' : ''}`}></span>
                <span className={`bar bar-4 ${fontSize === 'x-large' ? 'active' : ''}`}></span>
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Action Bar */}
      <div style={{ 
        position: 'fixed',
        top: 'var(--guest-header-h, 70px)',
        left: 0,
        right: 0,
        zIndex: 99,
        background: 'var(--color-bg-primary)',
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        padding: '12px 16px',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {canEdit && !isExpired && (
            <button 
              onClick={handleOpenAddItem}
              style={{
                padding: '8px 16px',
                background: 'var(--color-bg-primary)',
                color: 'var(--color-text-secondary)',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
              }}
            >
              + Add
            </button>
          )}
          {collection.collection.collection_type === 'marketplace' && (
            <button 
              onClick={() => window.location.href = '/marketplace'}
              style={{
                padding: '8px 16px',
                background: 'var(--color-bg-primary)',
                color: 'var(--color-text-secondary)',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
              }}
            >
              Marketplace
            </button>
          )}
          {collection.collection.collection_type === 'marketplace' && selectedItems.size === 0 && (
            <button 
              onClick={() => { setContactReviewItems(undefined); setShowContactModal(true); }}
              style={{
                padding: '8px 16px',
                background: 'var(--color-bg-primary)',
                color: 'var(--color-text-secondary)',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"></line>
                <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
              </svg>
              Contact
            </button>
          )}
          <button 
            onClick={() => window.location.href = '/help'}
            style={{
              padding: '8px',
              background: 'var(--color-bg-primary)',
              color: 'var(--color-text-secondary)',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            title="About"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
          </button>
        </div>
        
        {/* Right side buttons */}
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          {/* Guest profile button — hidden for marketplace */}
          {collection.collection.collection_type !== 'marketplace' && (
            <button
              onClick={() => setShowGuestProfileModal(true)}
              style={{
                background: 'var(--color-bg-primary)',
                border: '1px solid #d1d5db',
                borderRadius: '50%',
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                padding: '0',
              }}
              title={guestName ? `Signed in as ${guestName}` : 'Edit profile'}
              aria-label="Edit guest profile"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--color-text-muted)" strokeWidth="2">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            </button>
          )}
          
          {/* Theme toggle — light ↔ dark */}
          <button
            onClick={() => setGuestTheme(prev => prev === 'light' ? 'dark' : 'light')}
            style={{
              background: 'transparent',
              border: 'none',
              padding: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            title={`Theme: ${guestTheme}`}
            aria-label="Toggle theme"
          >
            {guestTheme === 'light' ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="5"></circle>
                <line x1="12" y1="1" x2="12" y2="3"></line>
                <line x1="12" y1="21" x2="12" y2="23"></line>
                <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                <line x1="1" y1="12" x2="3" y2="12"></line>
                <line x1="21" y1="12" x2="23" y2="12"></line>
                <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
                <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
              </svg>
            )}
          </button>

          {/* View toggle icon */}
          <button 
            onClick={() => setThumbnailSize(thumbnailSize === 'medium' ? 'x-large' : 'medium')}
            style={{
              background: 'transparent',
              border: 'none',
              padding: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            title={thumbnailSize === 'medium' ? 'Switch to grid view' : 'Switch to list view'}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="7" height="7"></rect>
              <rect x="14" y="3" width="7" height="7"></rect>
              <rect x="14" y="14" width="7" height="7"></rect>
              <rect x="3" y="14" width="7" height="7"></rect>
            </svg>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="guest-content">
      {/* Shared Collection Header - first element in scrollable content (hidden for marketplace) */}
      {collection.collection.collection_type !== 'marketplace' && (
        <SharedCollectionHeader
          ownerName={collection.collection.owner_name}
          ownerEmail={collection.collection.owner_email}
          collectionName={collection.collection.name}
          collectionDescription={collection.collection.description}
          itemCount={collection.items.length}
        />
      )}

      {/* Purchase hint for marketplace */}
      {collection.collection.collection_type === 'marketplace' && (
        <div style={{
          padding: '8px 16px',
          background: 'var(--color-accent-light, #eff6ff)',
          borderBottom: '1px solid var(--color-border)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-text-secondary)',
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="16" x2="12" y2="12"></line>
            <line x1="12" y1="8" x2="12.01" y2="8"></line>
          </svg>
          <span>
            To purchase, select items and contact the seller
            {collection.collection.distance ? (
              <span> · <strong>{collection.collection.distance.label}</strong> away</span>
            ) : collection.collection.owner_location ? (
              <span> · {collection.collection.owner_location}</span>
            ) : null}
          </span>
        </div>
      )}

      {/* Collection description — shown above items, matching the app */}
      {collection.collection.description && (
        <div style={{
          padding: '6px 16px 12px',
          color: 'var(--color-text-muted)',
          fontSize: 'var(--font-size-md)',
          lineHeight: '1.5',
          fontWeight: '500',
          textAlign: 'center',
          background: 'var(--color-bg-secondary)',
          whiteSpace: 'pre-wrap',
        }}>
          {collection.collection.description}
        </div>
      )}

      {/* Shopping List Controls: Manage/Shop toggle and Save button */}
      {collection.collection.is_checklist && collection.collection.stock_keeping && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 20px',
          background: 'var(--color-bg-secondary)',
          borderBottom: '1px solid #e5e7eb',
          gap: '12px'
        }}>
          {/* Left side: Mode toggle */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              onClick={() => setStockChecklistMode('manage')}
              style={{
                padding: '6px 12px',
                background: stockChecklistMode === 'manage' ? 'var(--color-accent-secondary)' : 'var(--color-bg-primary)',
                color: stockChecklistMode === 'manage' ? 'var(--color-text-inverse)' : 'var(--color-text-muted)',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
              }}
            >
              Manage
            </button>
            <button
              onClick={() => setStockChecklistMode('shop')}
              style={{
                padding: '6px 12px',
                background: stockChecklistMode === 'shop' ? 'var(--color-accent-secondary)' : 'var(--color-bg-primary)',
                color: stockChecklistMode === 'shop' ? 'var(--color-text-inverse)' : 'var(--color-text-muted)',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
              }}
            >
              Shop
            </button>
            
            {/* Show Qty: 0 checkbox - only in Shop mode */}
            {stockChecklistMode === 'shop' && (
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '13px', marginLeft: '8px', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
                <input
                  type="checkbox"
                  checked={showAllStockChecklistItems}
                  onChange={(e) => setShowAllStockChecklistItems(e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                <span style={{ whiteSpace: 'nowrap' }}>Qty: 0</span>
              </label>
            )}
          </div>
          
          {/* Right side: Save + Undo controls - only show if there are changes */}
          {((stockChecklistMode === 'shop' && collection.items.filter(item => item.is_checked).length > 0) ||
            (stockChecklistMode === 'manage' && Object.keys(pendingQuantityChanges).length > 0)) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              onClick={async () => {
                if (!token) return;
                
                if (stockChecklistMode === 'shop') {
                  // Shop mode: Save checked items (increment quantity, uncheck)
                  const checkedItems = collection.items.filter(item => item.is_checked);
                  if (checkedItems.length === 0) {
                    alert('No items checked');
                    return;
                  }
                  
                  try {
                    const fingerprint = await getGuestFingerprint();
                    let successCount = 0;
                    let errorCount = 0;
                    
                    for (const item of checkedItems) {
                      try {
                        // Step 1: Increment quantity
                        await updateItemAsGuest(token, item.id, fingerprint, {
                          quantity: (item.quantity ?? 0) + 1
                        });
                        
                        // Step 2: Uncheck item
                        await toggleItemCheckGuest(token, item.id, false);
                        
                        successCount++;
                      } catch (itemError) {
                        console.error(`Failed to process item ${item.id}:`, itemError);
                        errorCount++;
                      }
                    }
                    
                    await loadCollection(true);
                    
                    if (errorCount > 0) {
                      alert(`Failed to update ${errorCount} item${errorCount !== 1 ? 's' : ''}`);
                    }
                  } catch (error) {
                    console.error('Failed to save checklist:', error);
                    alert('Failed to save. Please try again.');
                  }
                } else {
                  // Manage mode: Save quantity changes
                  try {
                    const fingerprint = await getGuestFingerprint();
                    
                    for (const [idStr, qty] of Object.entries(pendingQuantityChanges)) {
                      const id = parseInt(idStr);
                      await updateItemAsGuest(token, id, fingerprint, { quantity: qty });
                    }
                    
                    await loadCollection(true);
                    setPendingQuantityChanges({});
                  } catch (error) {
                    console.error('Failed to save manage changes:', error);
                    alert('Failed to save. Please try again.');
                  }
                }
              }}
              style={{
                padding: '8px 16px',
                background: 'var(--color-accent-secondary)',
                color: 'var(--color-text-inverse)',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
              }}
            >
              Save
            </button>
            <button
              onClick={async () => {
                if (stockChecklistMode === 'manage') {
                  // Undo quantity edits by clearing pending changes
                  setPendingQuantityChanges({});
                  return;
                }

                // Shop mode: Undo by unchecking all currently checked items
                if (!token) return;

                const checkedItems = collection.items.filter(item => item.is_checked);
                if (checkedItems.length === 0) {
                  return;
                }

                try {
                  for (const item of checkedItems) {
                    await toggleItemCheckGuest(token, item.id, false);
                  }

                  await loadCollection(true);
                } catch (error) {
                  console.error('Failed to undo checklist changes:', error);
                  alert('Failed to undo changes. Please try again.');
                }
              }}
              style={{
                padding: '8px 16px',
                background: 'var(--color-bg-primary)',
                color: 'var(--color-text-muted)',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"></path>
                <path d="M3 3v5h5"></path>
              </svg>
            </button>
          </div>
          )}
        </div>
      )}

      {/* Notices — hidden for marketplace */}
      {collection.collection.collection_type !== 'marketplace' && (
        <>
          {isExpired && (
            <div className="notice-banner expired">
              ⚠️ This link has expired and is now read-only
            </div>
          )}
          {!canEdit && !isExpired && (
            <div className="notice-banner view-only">
              👁️ You have view-only access to this collection
            </div>
          )}
        </>
      )}

      {/* Items - Unified View */}
      <InventoryContent
        items={collection.items
          .filter(item => {
            // Apply Qty: 0 filter in Shop mode
            if (collection.collection.is_checklist && collection.collection.stock_keeping && 
                stockChecklistMode === 'shop' && showAllStockChecklistItems) {
              return (item.quantity ?? 1) === 0;
            }
            return true;
          })
          .map(item => ({
            ...item,
            // Apply pending quantity changes for display
            quantity: pendingQuantityChanges[item.id] ?? item.quantity,
            // Guests can edit items if they have edit permission, regardless of who created them
            // But delete is restricted to items they own (handled by context menu)
            // For marketplace: don't set my_role='viewer' to avoid lock icon
            my_role: collection.collection.collection_type === 'marketplace'
              ? undefined
              : (!canEdit || isExpired || !canEditItem(item)) ? 'viewer' : undefined
          }))}
        viewMode={
          collection.collection.collection_type === 'marketplace'
            ? 'marketplace-card'
            : collection.collection.is_checklist && collection.collection.stock_keeping
            ? (stockChecklistMode === 'shop' ? 'checklist-card' : 'stock-card')
            : collection.collection.is_checklist
            ? 'checklist-card'
            : collection.collection.stock_keeping
            ? 'stock-card'
            : 'card'
        }
        thumbnailSize={thumbnailSize}
        token={token}
        isGuestToken={true}
        isSharedCollection={collection.collection.collection_type !== 'marketplace'}
        selectedIds={collection.collection.collection_type === 'marketplace' ? selectedItems : new Set<number>()}
        isSelectMode={collection.collection.collection_type === 'marketplace'}
        isEditMode={false}
        onItemClick={collection.collection.collection_type === 'marketplace'
          ? toggleItemSelection
          : canEdit && !isExpired ? (item: any) => {
              // Allow guests to edit items they own by clicking on them
              if (canEditItem(item)) {
                handleOpenEditItem(item);
              }
            } : undefined}
        onMenuClick={canEdit && !isExpired ? (item: any) => {
          if (canDeleteItem(item)) {
            openGuestContextMenu(item);
          }
        } : undefined}
        onCheckboxToggle={collection.collection.is_checklist ? (item: any, e: React.MouseEvent) => {
          handleToggleCheck(item.id, item.is_checked);
        } : undefined}
        onQuantityChange={canEdit && !isExpired && stockChecklistMode === 'manage' ? (itemId, newQuantity) => {
          setPendingQuantityChanges(prev => ({ ...prev, [itemId]: newQuantity }));
        } : undefined}
        showQuantityInName={collection.collection.is_checklist && collection.collection.stock_keeping && stockChecklistMode === 'shop'}
        onOpenImageSelection={canEdit && !isExpired ? (itemId: number) => {
          const item = collection.items.find(i => i.id === itemId);
          if (item) {
            handleOpenImageSelection(item);
          }
        } : undefined}
        disableNameEdit={collection.collection.collection_type === 'marketplace'}
        highlightZeroQuantity={collection.collection.collection_type === 'marketplace'}
      />

      {/* Guest context menu */}
      {showGuestContextMenu && guestContextItem && createPortal(
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.25)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
          }}
          onClick={() => setShowGuestContextMenu(false)}
          onContextMenu={(e) => e.preventDefault()}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            onContextMenu={(e) => e.preventDefault()}
            style={{
              position: 'relative',
              background: 'var(--color-bg-primary)',
              borderRadius: 12,
              padding: '16px',
              minWidth: 220,
              boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
              display: 'flex',
              flexDirection: 'column',
              gap: 0,
            }}
          >
            {/* Close button */}
            <button
              onClick={() => setShowGuestContextMenu(false)}
              style={{
                position: 'absolute',
                top: 8,
                right: 8,
                border: 'none',
                background: 'transparent',
                fontSize: '24px',
                lineHeight: '24px',
                cursor: 'pointer',
                padding: '4px',
                color: 'var(--color-text-muted)',
              }}
            >
              ×
            </button>

            {/* Edit button */}
            <button
              style={{
                border: 'none',
                background: 'transparent',
                textAlign: 'left',
                padding: '12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                width: '100%',
                fontSize: '15px',
                borderRadius: 8,
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-bg-tertiary)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              onClick={() => {
                setShowGuestContextMenu(false);
                handleOpenEditItem(guestContextItem);
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
              </svg>
              <span>Edit</span>
            </button>

            {/* Change Image button */}
            <button
              style={{
                border: 'none',
                background: 'transparent',
                textAlign: 'left',
                padding: '12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                width: '100%',
                fontSize: '15px',
                borderRadius: 8,
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--color-bg-tertiary)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              onClick={() => {
                setShowGuestContextMenu(false);
                handleOpenImageSelection(guestContextItem);
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <circle cx="8.5" cy="8.5" r="1.5"></circle>
                <polyline points="21 15 16 10 5 21"></polyline>
              </svg>
              <span>Change Image</span>
            </button>

            {/* Delete button */}
            <button
              style={{
                border: 'none',
                background: 'transparent',
                textAlign: 'left',
                padding: '12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                width: '100%',
                fontSize: '15px',
                color: '#DC2626',
                borderRadius: 8,
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#fee'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              onClick={() => {
                setShowGuestContextMenu(false);
                handleRemoveItem(guestContextItem.id);
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
              </svg>
              <span>Delete</span>
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Guest name prompt */}
      {showNamePrompt && (
        <div className="signup-prompt-overlay">
          <div className="signup-prompt">
            <button className="close-prompt" onClick={() => setShowNamePrompt(false)}>×</button>
            <div className="prompt-icon">👋</div>
            <h3>What's your name?</h3>
            <p className="prompt-subtitle">So others know who added items</p>
            <form onSubmit={(e) => {
              e.preventDefault();
              if (guestName.trim()) {
                storeGuestDisplayName(guestName.trim());
                setShowNamePrompt(false);
                // Open add item modal after name is set
                handleOpenAddItem();
              }
            }}>
              <input
                type="text"
                value={guestName}
                onChange={(e) => setGuestName(e.target.value)}
                placeholder="Enter your name"
                className="name-input"
                autoFocus
                required
              />
              <button type="submit" className="signup-btn" disabled={!guestName.trim()}>
                Continue
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Sign-up prompt */}
      {showSignUpPrompt && (
        <div className="signup-prompt-overlay">
          <div className="signup-prompt">
            <button className="close-prompt" onClick={() => {
              setShowSignUpPrompt(false);
              localStorage.setItem('guest_signup_dismissed', 'true');
            }}>×</button>
            <div className="prompt-icon">🚀</div>
            <h3>Love this? Get your own!</h3>
            <p className="prompt-subtitle">Create unlimited collections and share with anyone</p>
            <div className="benefits-grid">
              <div className="benefit">
                <span className="benefit-icon">📋</span>
                <span>Unlimited collections</span>
              </div>
              <div className="benefit">
                <span className="benefit-icon">👥</span>
                <span>Collaborate with teams</span>
              </div>
              <div className="benefit">
                <span className="benefit-icon">📱</span>
                <span>Access anywhere</span>
              </div>
              <div className="benefit">
                <span className="benefit-icon">🔒</span>
                <span>Private & secure</span>
              </div>
            </div>
            <div className="signup-actions">
              <button 
                className="signup-btn"
                onClick={() => {
                  localStorage.setItem('guest_signup_prompt_shown', 'true');
                  navigate('/signup');
                }}
              >
                Get Started Free →
              </button>
              <button 
                className="login-btn"
                onClick={() => navigate('/login')}
              >
                I have an account
              </button>
            </div>
            <button className="maybe-later" onClick={() => {
              setShowSignUpPrompt(false);
              localStorage.setItem('guest_signup_dismissed', 'true');
            }}>
              Continue as guest
            </button>
          </div>
        </div>
      )}

      {/* Floating bottom bar for marketplace selection */}
      {collection.collection.collection_type === 'marketplace' && selectedItems.size > 0 && (
        <div style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          background: 'var(--color-bg-primary)',
          borderTop: '1px solid var(--color-border)',
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--color-text-secondary)' }}>
              {selectedItems.size} {selectedItems.size === 1 ? 'item' : 'items'} selected
            </span>
            <button
              onClick={() => setSelectedItems(new Set())}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--color-text-muted)',
                fontSize: '13px',
                cursor: 'pointer',
                textDecoration: 'underline',
                padding: 0,
              }}
            >
              Clear
            </button>
          </div>
          <button
            onClick={openReviewSheet}
            style={{
              padding: '10px 20px',
              background: 'var(--color-accent)',
              color: '#fff',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13"></line>
              <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
            </svg>
            Contact Seller
          </button>
        </div>
      )}

      {/* Review sheet modal */}
      {showReviewSheet && createPortal(
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            zIndex: 9999,
            touchAction: 'none',
          }}
          onClick={() => setShowReviewSheet(false)}
          onTouchMove={(e) => e.preventDefault()}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            onTouchMove={(e) => e.stopPropagation()}
            style={{
              background: 'var(--color-bg-primary)',
              borderRadius: '16px 16px 0 0',
              width: '100%',
              maxWidth: 480,
              maxHeight: '80vh',
              overflow: 'auto',
              overscrollBehavior: 'contain',
              padding: '20px 16px',
              boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
            }}
          >
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: 'var(--color-text-primary)' }}>
                Review Your Order
              </h3>
              <button
                onClick={() => setShowReviewSheet(false)}
                style={{ background: 'none', border: 'none', fontSize: '24px', color: 'var(--color-text-muted)', cursor: 'pointer', padding: '4px' }}
              >×</button>
            </div>

            {/* Items */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
              {reviewItems.map((item, idx) => (
                <div key={item.id} style={{
                  padding: '12px',
                  borderRadius: '10px',
                  border: '1px solid var(--color-border)',
                  background: item.price !== item.originalPrice ? 'rgba(249, 115, 22, 0.08)' : 'var(--color-bg-secondary)',
                }}>
                  <div style={{ fontWeight: '500', fontSize: '15px', color: 'var(--color-text-primary)', marginBottom: '8px' }}>
                    #{item.id} — {item.name}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'nowrap' }}>
                    {/* Qty stepper */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                      <label style={{ fontSize: '13px', color: 'var(--color-text-muted)', marginRight: '2px' }}>Qty</label>
                      <button
                        onClick={() => {
                          setReviewItems(prev => prev.map((r, i) => i === idx ? { ...r, qty: Math.max(1, r.qty - 1) } : r));
                        }}
                        disabled={item.qty <= 1}
                        style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '6px',
                          border: '1px solid var(--color-border)',
                          background: item.qty <= 1 ? 'var(--color-bg-tertiary)' : 'var(--color-bg-primary)',
                          color: item.qty <= 1 ? 'var(--color-text-faint)' : 'var(--color-text-primary)',
                          fontSize: '16px',
                          fontWeight: '600',
                          cursor: item.qty <= 1 ? 'default' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: 0,
                        }}
                      >−</button>
                      <input
                        type="text"
                        inputMode="numeric"
                        defaultValue={item.qty}
                        key={`qty-${item.id}-${item.qty}`}
                        onBlur={(e) => {
                          const val = Math.min(item.maxQty, Math.max(1, parseInt(e.target.value) || 1));
                          e.target.value = String(val);
                          setReviewItems(prev => prev.map((r, i) => i === idx ? { ...r, qty: val } : r));
                        }}
                        style={{
                          width: '36px',
                          padding: '4px 2px',
                          borderRadius: '6px',
                          border: '1px solid var(--color-border)',
                          background: 'var(--color-bg-primary)',
                          color: 'var(--color-text-primary)',
                          fontSize: '14px',
                          textAlign: 'center',
                        }}
                      />
                      <button
                        onClick={() => {
                          setReviewItems(prev => prev.map((r, i) => i === idx ? { ...r, qty: Math.min(r.maxQty, r.qty + 1) } : r));
                        }}
                        disabled={item.qty >= item.maxQty}
                        style={{
                          width: '28px',
                          height: '28px',
                          borderRadius: '6px',
                          border: '1px solid var(--color-border)',
                          background: item.qty >= item.maxQty ? 'var(--color-bg-tertiary)' : 'var(--color-bg-primary)',
                          color: item.qty >= item.maxQty ? 'var(--color-text-faint)' : 'var(--color-text-primary)',
                          fontSize: '16px',
                          fontWeight: '600',
                          cursor: item.qty >= item.maxQty ? 'default' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: 0,
                        }}
                      >+</button>
                      {item.maxQty > 1 && (
                        <span style={{ fontSize: '11px', color: 'var(--color-text-faint)', marginLeft: '2px' }}>
                          /{item.maxQty}
                        </span>
                      )}
                    </div>
                    {/* Price */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                      <label style={{ fontSize: '13px', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>$</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        defaultValue={item.price.toFixed(2)}
                        key={`price-${item.id}`}
                        onBlur={(e) => {
                          const val = Math.max(0, parseFloat(e.target.value) || 0);
                          e.target.value = val.toFixed(2);
                          setReviewItems(prev => prev.map((r, i) => i === idx ? { ...r, price: val } : r));
                        }}
                        style={{
                          width: '64px',
                          padding: '6px 6px',
                          borderRadius: '6px',
                          border: `1px solid ${item.price !== item.originalPrice ? 'var(--color-warning)' : 'var(--color-border)'}`,
                          background: 'var(--color-bg-primary)',
                          color: 'var(--color-text-primary)',
                          fontSize: '14px',
                          textAlign: 'center',
                        }}
                      />
                    </div>
                    {/* Subtotal */}
                    <span style={{ marginLeft: 'auto', fontSize: '14px', fontWeight: '600', color: 'var(--color-text-primary)', flexShrink: 0, whiteSpace: 'nowrap' }}>
                      ${(item.qty * item.price).toFixed(2)}
                    </span>
                  </div>
                  {/* Offer indicator */}
                  {item.price !== item.originalPrice && (
                    <div style={{ fontSize: '12px', color: 'var(--color-warning)', marginTop: '6px' }}>
                      Offer — listed at ${item.originalPrice.toFixed(2)}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Total + Send */}
            <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ fontSize: '16px', fontWeight: '600', color: 'var(--color-text-primary)' }}>Total</span>
                <span style={{ fontSize: '18px', fontWeight: '700', color: 'var(--color-text-primary)' }}>
                  ${reviewItems.reduce((sum, i) => sum + i.price * i.qty, 0).toFixed(2)}
                </span>
              </div>
              <button
                onClick={() => {
                  setContactReviewItems(reviewItems.map(i => ({ id: i.id, name: i.name, qty: i.qty, maxQty: i.maxQty, price: i.price, originalPrice: i.originalPrice })));
                  setShowReviewSheet(false);
                  setShowContactModal(true);
                }}
                style={{
                  width: '100%',
                  padding: '14px',
                  background: 'var(--color-accent)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg>
                Contact Seller
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Contact Seller Modal */}
      {collection && collection.collection.collection_type === 'marketplace' && (
        <ContactSellerModal
          open={showContactModal}
          onClose={() => { setShowContactModal(false); setContactReviewItems(undefined); }}
          sellerId={collection.collection.owner_id}
          sellerName={collection.collection.owner_name || 'Seller'}
          collectionId={collection.collection.id}
          reviewItems={contactReviewItems}
        />
      )}

      {/* Footer */}
      <div className="guest-footer">
        <p>
          Powered by <strong>Wandering Sock</strong> - Your collaborative inventory app
        </p>
        <button onClick={() => navigate('/signup')} className="footer-signup-btn">
          Create Your Account
        </button>
      </div>

      {/* Item Edit Modal */}
      <UnifiedItemEditModal
        mode="guest"
        open={showItemEditModal}
        item={editingItem ? {
          id: editingItem.id || -1,
          name: editingItem.name || '',
          description: editingItem.description || '',
          color: '',
          area: '',
          location: '',
        } : {
          id: -1,
          name: newItemData.name || '',
          description: newItemData.description || '',
          color: '',
          area: '',
          location: '',
        }}
        onClose={() => {
          setShowItemEditModal(false);
          setEditingItem(null);
          setNewItemData({name: '', description: ''});
        }}
        onChange={(patch) => {
          // Update editingItem or newItemData with changes from modal
          if (editingItem) {
            setEditingItem({ ...editingItem, ...patch });
          } else {
            // Adding new item - update newItemData
            setNewItemData(prev => ({ ...prev, ...patch }));
          }
        }}
        onSave={async () => {
          // Guest mode: use the updated state
          if (editingItem) {
            // Editing existing item
            await handleSaveItem(editingItem.name || '', editingItem.description || '');
          } else {
            // Adding new item
            await handleSaveItem(newItemData.name || '', newItemData.description || '');
          }
          // Close modal and reset state after successful save
          setShowItemEditModal(false);
          setEditingItem(null);
          setNewItemData({name: '', description: ''});
        }}
      />

      {/* Image Selection Modal */}
      {showImageSelectionModal && selectedItemForImage && (
        <ImageSelectionModal
          isOpen={showImageSelectionModal}
          onClose={() => {
            setShowImageSelectionModal(false);
            setSelectedItemForImage(null);
            setImageSearchResults([]);
            setImagePhotoData([]);
          }}
          itemName={selectedItemForImage?.name || 'Item'}
          searchImages={Array.isArray(imageSearchResults) ? imageSearchResults : []}
          initials={getInitials(selectedItemForImage?.name || 'Item')}
          color1={getColorFromName(selectedItemForImage?.name || 'Item', 0)}
          color2={getColorFromName(selectedItemForImage?.name || 'Item', 12345)}
          currentImageId={selectedItemForImage?.image_id || null}
          imageSourceType={selectedItemForImage?.image_id ? 'unsplash' : undefined}
          onSelectSearchImage={handleSelectSearchImage}
          onSelectInitials={handleSelectInitials}
          onTakePhoto={handleTakePhoto}
          onSearchKeywords={async (keywords: string) => {
            if (!keywords.trim()) return;
            
            try {
              setImageSearchLoading(true);
              
              const accessKey = process.env.REACT_APP_UNSPLASH_ACCESS_KEY;
              if (!accessKey) {
                console.error('[Guest] Unsplash access key not configured');
                return;
              }
              
              const { buildUnsplashSearchUrl } = await import('../utils/unsplashUtils');
              const searchUrl = buildUnsplashSearchUrl(keywords.trim(), accessKey);
              
              const response = await fetch(searchUrl);
              if (!response.ok) {
                throw new Error(`Unsplash API error: ${response.status}`);
              }
              
              const data = await response.json();
              
              if (data.results && Array.isArray(data.results)) {
                const imageUrls = data.results.map((photo: any) => photo.urls?.small).filter(Boolean);
                setImagePhotoData(data.results);
                setImageSearchResults(imageUrls);
              } else {
                setImageSearchResults([]);
                setImagePhotoData([]);
              }
            } catch (error) {
              console.error('[Guest] Failed to search images:', error);
              alert('Failed to search images. Please try again.');
            } finally {
              setImageSearchLoading(false);
            }
          }}
          isLoading={imageSearchLoading}
        />
      )}

      {/* Why Sign Up Modal */}
      {showWhySignUpModal && (
        <WhySignUpModal onClose={() => setShowWhySignUpModal(false)} />
      )}

      {/* Guest Profile Modal */}
      <GuestProfileModal
        isOpen={showGuestProfileModal}
        onClose={() => setShowGuestProfileModal(false)}
        currentName={guestName}
        onSave={async (newName) => {
          try {
            // Update in backend
            const fingerprint = await getGuestFingerprint();
            const response = await fetch(`/api/collections/guest/${token}/profile`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                display_name: newName,
                device_fingerprint: fingerprint
              })
            });
            
            if (!response.ok) {
              throw new Error('Failed to update profile');
            }
            
            // Update local state and storage
            setGuestName(newName);
            storeGuestDisplayName(newName);
            
            // Reload collection to get updated name in items
            await loadCollection(true);
          } catch (error) {
            console.error('Failed to update guest profile:', error);
            throw error; // Let modal handle the error
          }
        }}
      />
      </div>
    </div>
  );
};

export default GuestCollectionView;
